// Conditional Logic Utility Functions
import { ConditionalRule, Condition } from '../types/conditional-logic.types';

/**
 * Evaluate all conditions for a given set of rules
 * @param rules - Array of conditional rules to evaluate
 * @param formData - Current form field values
 * @returns boolean - Whether conditions are met
 */
export function evaluateConditions(rules: ConditionalRule[], formData: Record<string, any>): boolean {
  if (rules.length === 0) return true;

  // Evaluate each rule
  return rules.some(rule => {
    if (!rule.enabled) return false;

    const conditionsResult = rule.conditions.map(condition =>
      evaluateCondition(condition, formData)
    );

    // Apply logic operator (AND/OR)
    if (rule.logicOperator === 'AND') {
      return conditionsResult.every(result => result);
    } else {
      return conditionsResult.some(result => result);
    }
  });
}

/**
 * Evaluate a single condition
 * @param condition - Condition to evaluate
 * @param formData - Current form field values
 * @returns boolean - Whether condition is met
 */
export function evaluateCondition(condition: Condition, formData: Record<string, any>): boolean {
  const fieldValue = formData[condition.fieldName];
  const conditionValue = condition.value;

  switch (condition.operator) {
    // Text operators
    case 'equals':
      return fieldValue == conditionValue;

    case 'not_equals':
      return fieldValue != conditionValue;

    case 'contains':
      return String(fieldValue || '').toLowerCase().includes(String(conditionValue).toLowerCase());

    case 'not_contains':
      return !String(fieldValue || '').toLowerCase().includes(String(conditionValue).toLowerCase());

    case 'starts_with':
      return String(fieldValue || '').toLowerCase().startsWith(String(conditionValue).toLowerCase());

    case 'ends_with':
      return String(fieldValue || '').toLowerCase().endsWith(String(conditionValue).toLowerCase());

    case 'is_empty':
      return !fieldValue || fieldValue === '' || (Array.isArray(fieldValue) && fieldValue.length === 0);

    case 'is_not_empty':
      return !!fieldValue && fieldValue !== '' && !(Array.isArray(fieldValue) && fieldValue.length === 0);

    // Numeric operators
    case 'greater_than':
      return Number(fieldValue) > Number(conditionValue);

    case 'less_than':
      return Number(fieldValue) < Number(conditionValue);

    case 'greater_than_or_equal':
      return Number(fieldValue) >= Number(conditionValue);

    case 'less_than_or_equal':
      return Number(fieldValue) <= Number(conditionValue);

    case 'between':
      return Number(fieldValue) >= Number(conditionValue) &&
             Number(fieldValue) <= Number(condition.secondValue);

    // Choice operators
    case 'is_selected':
      if (Array.isArray(fieldValue)) {
        return fieldValue.includes(conditionValue);
      }
      return fieldValue === conditionValue;

    case 'is_not_selected':
      if (Array.isArray(fieldValue)) {
        return !fieldValue.includes(conditionValue);
      }
      return fieldValue !== conditionValue;

    case 'includes':
      return Array.isArray(fieldValue) && fieldValue.includes(conditionValue);

    case 'not_includes':
      return !Array.isArray(fieldValue) || !fieldValue.includes(conditionValue);

    // Date operators
    case 'before':
      return new Date(fieldValue) < new Date(conditionValue);

    case 'after':
      return new Date(fieldValue) > new Date(conditionValue);

    case 'on':
      return new Date(fieldValue).toDateString() === new Date(conditionValue).toDateString();

    case 'between_dates':
      const date = new Date(fieldValue);
      return date >= new Date(conditionValue) && date <= new Date(condition.secondValue);

    case 'is_today':
      return new Date(fieldValue).toDateString() === new Date().toDateString();

    case 'is_past':
      return new Date(fieldValue) < new Date();

    case 'is_future':
      return new Date(fieldValue) > new Date();

    // Boolean operators
    case 'is_true':
      return fieldValue === true || fieldValue === 'true' || fieldValue === 1 || fieldValue === '1';

    case 'is_false':
      return fieldValue === false || fieldValue === 'false' || fieldValue === 0 || fieldValue === '0';

    // File operators
    case 'is_uploaded':
      return !!fieldValue && (typeof fieldValue === 'object' || typeof fieldValue === 'string');

    case 'is_not_uploaded':
      return !fieldValue;

    default:
      return false;
  }
}

/**
 * Get fields that should be visible based on conditional logic
 * @param allFields - All form fields
 * @param rules - Conditional rules
 * @param formData - Current form data
 * @returns Array of visible field IDs
 */
export function getVisibleFieldIds(
  allFields: string[],
  rules: ConditionalRule[],
  formData: Record<string, any>
): string[] {
  const visibleFields = new Set(allFields);

  rules.forEach(rule => {
    if (!rule.enabled) return;

    const conditionsMet = evaluateConditions([rule], formData);

    rule.actions.forEach(action => {
      if (action.type === 'show_field' && conditionsMet) {
        action.targetFieldNames.forEach(fieldName => visibleFields.add(fieldName));
      } else if (action.type === 'hide_field' && conditionsMet) {
        action.targetFieldNames.forEach(fieldName => visibleFields.delete(fieldName));
      }
    });
  });

  return Array.from(visibleFields);
}
